import React, { useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";

import { PageLayout, PageHeader } from "../components/layout";
import { StatePanel } from "../components/ui";
import useBreakpoint from "../hooks/useBreakpoint";
import ChannelList from "./ChannelList";
import MessageThread from "./MessageThread";
import { useChatChannels } from "./useMerchantChat";
import "./rc-merchant-chat.scss";

/**
 * /messages — the merchant ↔ Retail Circle communication channels
 * (Roam-bridged). Two panes from 900px (channel rail + thread); below that
 * the panes become two screens with a back button, WhatsApp-style.
 *
 * The channel set is decided server-side: store admins get their store's
 * private "<Store> x Retail Circle" channel, owners additionally get the
 * read-only Retail Circle Updates channel, master admins see everything.
 */
const MerchantChatPage = () => {
  const history = useHistory();
  const { channelId } = useParams();
  // Two panes need laptop-shaped room — the same 900px midpoint the
  // table → card switch uses (see useBreakpoint's isAtLeastMd docblock).
  const { isAtLeastMd } = useBreakpoint();
  const singlePane = !isAtLeastMd;

  const { data: channels = [], isLoading, isError, refetch } = useChatChannels();




  const activeId = channelId ? Number(channelId) : null;
  const activeChannel = useMemo(
    () => channels.find((channel) => channel.id === activeId) || null,
    [channels, activeId]
  );

  // Desktop lands on the first channel automatically; phones stay on the
  // list until a conversation is chosen.
  useEffect(() => {
    if (!singlePane && !activeId && channels.length) {
      history.replace(`/messages/${channels[0].id}`);
    }
  }, [singlePane, activeId, channels, history]);

  const showList = !singlePane || !activeChannel;
  const showThread = !singlePane || Boolean(activeChannel);

  let listBody = null;
  if (isLoading) {
    listBody = <StatePanel state="loading" />;
  } else if (isError) {
    listBody = <StatePanel state="error" action={{ label: "Retry", onClick: refetch }} />;
  } else if (!channels.length) {
    listBody = (
      <StatePanel
        state="empty"
        icon="MessageCircle"
        title="No conversations yet"
        description="Your channel with the Retail Circle team appears here once your store is set up."
      />
    );
  } else {
    listBody = (
      <ChannelList
        channels={channels}
        activeId={activeId}
        onSelect={(channel) => history.push(`/messages/${channel.id}`)}
      />
    );
  }

  return (
    <PageLayout className="rc-merchant-chat">
      <PageHeader
        title={singlePane && activeChannel ? activeChannel.name : "Messages"}
        description={
          singlePane && activeChannel
            ? undefined
            : "Chat with the Retail Circle team — messages sync with our virtual office."
        }
        // PageHeader renders the back control INLINE before the title, and
        // publishes it to the mobile topbar via usePublishPageChrome. Passing
        // it as `topbarActions` instead put a lone arrow on its own row under
        // the title on mobile (staging 2026-09-28).
        showBackButton={Boolean(singlePane && activeChannel)}
        onBack={() => history.push("/messages")}
      />

      <div className="rc-chat">
        {showList && <div className="rc-chat__rail">{listBody}</div>}
        {showThread && <MessageThread channel={activeChannel} />}
      </div>
    </PageLayout>
  );
};

export default MerchantChatPage;
